import { GDriveSheet } from 'infrastructure/googleDrive'
import { bulkCreateLeavesFromList } from '../TreeActions'
import { ILeafRequest, INode } from 'shared/types/tree'
import { allNodesOfType } from 'components/utility/TreeHelpers'
import { LeafTypes, NodeType } from 'shared/types/ingestion'
import { doInBatches, processInChunks } from 'shared/utils'
import { Standard } from 'shared/types'
import {
  requiredHeaders,
  requiredHeadersIndices,
  unitsToLessonsToStandards,
} from './types'
import { normalizeStandardName } from 'components/tree/standardUtilities'
import {
  appendNodeToTree,
  retrieveIds,
  NodeRequestParams,
} from '../TreeRequests'
import { getTree } from 'shared/hooks'

export class SheetStandardsHandler {
  unitToLessonsToStandardsMap: unitsToLessonsToStandards
  unitsAndLessons: { [unit: string]: { [lesson: string]: INode } }
  standardsMap: { [key: string]: Standard }
  sheet: GDriveSheet
  missingStandards: string[] = []

  constructor({
    unitToLessonsToStandardsMap,
    unitsAndLessons,
    standardsMap,
    missingStandards,
    sheet,
  }: {
    unitToLessonsToStandardsMap: unitsToLessonsToStandards
    unitsAndLessons: { [unit: string]: { [lesson: string]: INode } }
    standardsMap: { [key: string]: Standard }
    missingStandards: string[]
    sheet: GDriveSheet
  }) {
    this.unitsAndLessons = unitsAndLessons
    this.unitToLessonsToStandardsMap = unitToLessonsToStandardsMap
    this.standardsMap = standardsMap
    this.missingStandards = missingStandards
    this.sheet = sheet
  }

  static async create(
    sheet: GDriveSheet,
    standardsMap: Record<string, Standard>,
  ) {
    const tree = await getKiddomTree(sheet)
    const unitsAndLessons = getUnitsAndLessons(tree!)
    const unitToLessonToStandardsMap = getUnitToLessonToStandardsMap(sheet)
    const missingStandards = Object.values(unitToLessonToStandardsMap)
      .flatMap((unit) => Object.values(unit).flat())
      .filter((s) => !standardsMap[normalizeStandardName(s)])
      .map((s) => 'Missing Standard: ' + s)
    return new SheetStandardsHandler({
      unitToLessonsToStandardsMap: unitToLessonToStandardsMap,
      unitsAndLessons: unitsAndLessons,
      standardsMap: standardsMap,
      missingStandards: missingStandards,
      sheet: sheet,
    })
  }

  async addStandardListsToLessons() {
    await addStandardListsToLessons(
      this.unitToLessonsToStandardsMap,
      this.unitsAndLessons,
    )
    const tree = await getKiddomTree(this.sheet)
    this.unitsAndLessons = getUnitsAndLessons(tree!)
  }

  async addStandards() {
    return addStandardsToLessons(
      this.unitToLessonsToStandardsMap,
      this.unitsAndLessons,
      this.standardsMap,
    )
  }
}

function getKiddomTreeLink(sheet: GDriveSheet) {
  const cell = sheet.getCell({ row: 0, col: 0 })
  if (!cell.hyperlink)
    throw new Error('No Kiddom Link Found for sheet ' + sheet.properties.title)
  return cell.hyperlink
}

async function getKiddomTree(sheet: GDriveSheet) {
  const url = getKiddomTreeLink(sheet)
  const urlParts = url.split('/')
  const lastSegment = urlParts[urlParts.length - 1]
  const ids = lastSegment.split(':')
  const { result } = await getTree({ treeId: ids[0], versionId: ids[1] })
  return result
}

function getUnitsAndLessons(tree: INode) {
  const unitsAndLessons: any = {}
  const units = allNodesOfType(tree, NodeType.Unit)

  units.forEach((u) => {
    const unitNumber = u.title.split(':')[0].split(' ')[1]
    const unitLessons = allNodesOfType(u, NodeType.LessonPlan)
      .filter((l) => {
        return l.title.includes('Lesson')
      })
      .reduce(
        (acc, lesson) => {
          const lessonNumber = parseInt(
            lesson.title.split(':')[0].split(' ')[1],
          )
          acc[lessonNumber] = lesson
          return acc
        },
        {} as { [key: string]: INode },
      )
    unitsAndLessons[unitNumber] = unitLessons
  })
  return unitsAndLessons
}

function getHeaderColumns(sheet: GDriveSheet): requiredHeadersIndices {
  const headerRow = sheet.getRowValues(1)
  const headerColumns: { [key: string]: null | number } = {}
  Object.keys(requiredHeaders).forEach((key) => {
    if (isNaN(Number(key))) {
      headerColumns[key] = null
    }
  })
  headerRow.forEach((cell) => {
    if (cell) {
      for (const [key, value] of Object.entries(requiredHeaders)) {
        if (value === cell.trim().toUpperCase()) {
          headerColumns[key] = headerRow.indexOf(cell)
          break
        }
      }
    }
  })

  const missingHeaders: string[] = []
  for (const [key, value] of Object.entries(requiredHeaders)) {
    if (headerColumns[key] === null) missingHeaders.push(value)
  }
  if (missingHeaders.length > 0)
    throw new Error('Missing Headers: ' + missingHeaders.join(', '))

  return headerColumns as unknown as requiredHeadersIndices
}

function getUnitToLessonToStandardsMap(sheet: GDriveSheet): {
  [key: string]: { [key: string]: string[] }
} {
  const headerColumns = getHeaderColumns(sheet)
  const sheetRows = sheet.getRows()
  const unitToLessonToStandardsMap: {
    [key: string]: { [key: string]: string[] }
  } = {}
  for (let i = 2; i < sheetRows.length; i++) {
    const row = sheet.getRowValues(i)
    const unit = row[headerColumns.UNIT_NUMBER]
    const lesson = row[headerColumns.LESSON_NUMBER]
    const addressingStandards =
      row[headerColumns.ADDRESSING_STANDARDS]?.split('\n') || []
    if (!unit || !lesson || !addressingStandards) continue
    if (!unitToLessonToStandardsMap[unit]) unitToLessonToStandardsMap[unit] = {}
    if (!unitToLessonToStandardsMap[unit][lesson])
      unitToLessonToStandardsMap[unit][lesson] = []
    unitToLessonToStandardsMap[unit][lesson] = addressingStandards
      .map((s) => {
        return s.split(':')[0].split('-')[0].trim()
      })
      .filter((s) => s.trim().length > 0)
  }
  return unitToLessonToStandardsMap
}

function loopThroughUnitsAndLessons(
  unitToLessonsToStandardsMap: unitsToLessonsToStandards,
  unitsAndLessons: { [unit: string]: { [lesson: string]: INode } },
  callback: (lessonNode: INode, standards: string[]) => any,
) {
  const missingUnits: string[] = []
  const missingLessons: { [unit: string]: string[] } = {}
  let returnValue: any
  Object.keys(unitToLessonsToStandardsMap).forEach((unit) => {
    const unitNode = unitsAndLessons[unit]
    if (!unitNode) {
      missingUnits.push(unit)
      return
    }
    Object.keys(unitToLessonsToStandardsMap[unit]).forEach((lesson) => {
      const lessonNode = unitsAndLessons[unit][lesson]
      if (!lessonNode) {
        if (!missingLessons[unit]) missingLessons[unit] = []
        missingLessons[unit].push(lesson)
        return
      }
      returnValue = callback(
        lessonNode,
        unitToLessonsToStandardsMap[unit][lesson],
      )
    })
  })
  return { missingUnits, missingLessons, returnValue }
}

async function addStandardListsToLessons(
  unitToLessonsToStandardsMap: unitsToLessonsToStandards,
  unitsAndLessons: { [unit: string]: { [lesson: string]: INode } },
) {
  const standardListParams: NodeRequestParams[] = []

  function getStandardListParams(lessonNode: INode) {
    if (
      allNodesOfType(lessonNode, NodeType.SkillsList).find(
        (node) => node.title.toUpperCase().trim() === 'ADDRESSING STANDARDS',
      )
    )
      return

    // if there are no presentation nodes, add standard list to beginning of lesson
    const presentationListNode =
      allNodesOfType(lessonNode, NodeType.NodeList).find(
        (node) => node.body?.type === NodeType.Presentation,
      ) || lessonNode.nodes[0]

    const nextNode =
      lessonNode.nodes[lessonNode.nodes.indexOf(presentationListNode) + 1]
    const toAppend = {
      ...retrieveIds(lessonNode),
      next_sibling_node_id: nextNode?.id,
      title: 'Addressing Standards',
      type: NodeType.SkillsList,
    }

    standardListParams.push(toAppend)
  }

  loopThroughUnitsAndLessons(
    unitToLessonsToStandardsMap,
    unitsAndLessons,
    getStandardListParams,
  )

  await doInBatches(standardListParams, appendNodeToTree)
}

async function addStandardsToLessons(
  unitToLessonsToStandardsMap: unitsToLessonsToStandards,
  unitsAndLessons: { [unit: string]: { [lesson: string]: INode } },
  standardsMap: { [key: string]: Standard },
) {
  const leafParamsList: ILeafRequest[] = []
  const leafParams: { [key: string]: ILeafRequest[] } = {}

  function getStandardLeafParams(lessonNode: INode, standards: string[]) {
    const standardList = allNodesOfType(lessonNode, NodeType.SkillsList).find(
      (node) => node.title.toUpperCase().trim() === 'ADDRESSING STANDARDS',
    )
    if (!standardList) return
    if (!leafParams[standardList.id]) leafParams[standardList.id] = []
    const standardLeaves: ILeafRequest[] = standards
      .map((s) => {
        if (!standardsMap[normalizeStandardName(s)]) {
          return undefined
        }
        return {
          parent_id: standardList.id,
          type: LeafTypes.Skill,
          body: {
            skillID: standardsMap[normalizeStandardName(s)].standardId,
          },
        }
      })
      .filter((l) => l !== undefined) as ILeafRequest[]
    leafParamsList.push(...standardLeaves.filter((l) => l))
  }

  loopThroughUnitsAndLessons(
    unitToLessonsToStandardsMap,
    unitsAndLessons,
    getStandardLeafParams,
  )

  return processInChunks(leafParamsList, 25, (leaves: ILeafRequest[]) => {
    return bulkCreateLeavesFromList({
      treeId: Object.values(unitsAndLessons)[0][1].tree_id,
      versionId: Object.values(unitsAndLessons)[0][1].version,
      leaves: leaves,
    })
  })
}
